import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import Edit from "@mui/icons-material/Edit"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Box, Collapse, Stack, SxProps, Theme } from "@mui/material"
import { ReactElement, useState } from "react"

import { useScreenSize } from "../../hooks/useScreenSize"
import { GRID_SPACING } from "../../themes/defaultThemeParams"
import AppButton from "../buttons/appButton"
// import { NestedKeyValueListType } from "../dataDisplay/nestedVerticalKeyValueList"
import NestedVerticalKeyValueList, {
    NestedKeyValueListType,
} from "../dataDisplay/nestedVerticalKeyValueList"
import VerticalKeyValueList from "../dataDisplay/verticalKeyValueList"
import { AppCard, AppCardContent, AppCardHeader } from "../surfaces/appCard"
import DisplayIf from "../wrappers/displayIf"

interface FormPageSummaryProps {
    pageTitle: string
    fieldMapping:
        | Record<string, string | number | boolean | undefined>
        | NestedKeyValueListType
    onEdit?: () => void
    sx?: SxProps<Theme>
}

const isNestedKeyValueListType = (
    fieldMapping:
        | Record<string, string | number | boolean | undefined>
        | NestedKeyValueListType
): fieldMapping is NestedKeyValueListType =>
    typeof fieldMapping === "object" &&
    Object.keys(fieldMapping).length > 0 &&
    typeof Object.values(fieldMapping)[0] === "object"

/** Component used to display a summary of user inputted values for a form page */
const FormPageSummary = ({
    pageTitle,
    fieldMapping,
    onEdit = undefined,
    sx = {},
}: FormPageSummaryProps): ReactElement => {
    const [expanded, setExpanded] = useState(false)
    const { isMob } = useScreenSize()

    return (
        <AppCard
            sx={{ ...sx, paddingY: GRID_SPACING, px: { xs: 0, md: undefined } }}
        >
            <Stack
                direction={{ xs: "row", sm: "row" }}
                justifyContent="space-between"
                alignItems="center"
                spacing={{ xs: 2, sm: 0 }}
            >
                <Stack display="flex" direction="row" width="100%">
                    <DisplayIf condition={!isMob}>
                        <CheckCircleOutlineIcon
                            color="success"
                            sx={{ fontSize: "30px" }}
                        />
                    </DisplayIf>
                    <AppCardHeader
                        title={pageTitle}
                        titleTypographyProps={
                            isMob ? { variant: "body1" } : { variant: "h5" }
                        }
                        sx={{
                            marginBottom: 0,
                            marginLeft: { xs: 1, sm: 2 },
                            textOverflow: "ellipsis",
                        }}
                    />
                </Stack>

                <AppCardContent sx={{ width: "100%" }}>
                    <Stack
                        spacing={GRID_SPACING}
                        direction="row"
                        flexGrow={1}
                        justifyContent="flex-end"
                        sx={{
                            marginLeft: 2,
                        }}
                    >
                        {!!onEdit && (
                            <AppButton
                                size="medium"
                                variant="outlined"
                                onClick={onEdit}
                                endIcon={<Edit />}
                            >
                                Edit
                            </AppButton>
                        )}
                        <AppButton
                            size="medium"
                            variant="outlined"
                            endIcon={
                                expanded ? (
                                    <ExpandLessIcon />
                                ) : (
                                    <ExpandMoreIcon />
                                )
                            }
                            onClick={() => setExpanded(!expanded)}
                        >
                            {expanded ? "Hide" : "Show"}
                        </AppButton>
                    </Stack>
                </AppCardContent>
            </Stack>
            <Collapse
                in={expanded}
                unmountOnExit
                sx={{
                    marginLeft: 6,
                }}
            >
                {isNestedKeyValueListType(fieldMapping) ? (
                    <Box marginTop={2}>
                        <NestedVerticalKeyValueList
                            nestedKwargs={fieldMapping}
                            headerVairant="h6"
                        />
                    </Box>
                ) : (
                    <Box marginTop={2}>
                        <VerticalKeyValueList
                            kwargs={fieldMapping}
                            alwaysUseMobileTypography
                        />
                    </Box>
                )}
            </Collapse>
        </AppCard>
    )
}

FormPageSummary.defaultProps = {
    onEdit: undefined,
    sx: {},
}

export default FormPageSummary
